import React, { useState } from 'react';
import { Lock, Sparkles, X, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PremiumFeatureLock({ featureName, description, requiredPlan = 'Premium', onClose }) {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Premium',
      price: 4.99,
      features: [
        'Unlimited price alerts',
        'Advanced watchlist (50+ items)',
        'Chrome extension premium features',
        'Ad-free experience',
        'Email alerts for price drops',
        'Priority support'
      ]
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" data-testid="premium-lock-modal">
      <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-center relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            data-testid="close-premium-modal"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{featureName}</h2>
          <p className="text-purple-100 text-sm">{description}</p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-purple-900">
              <strong>🔒 Premium Feature</strong><br />
              This feature requires a {requiredPlan} subscription.
            </p>
          </div>

          {/* Premium Plan */}
          <div className="border-2 border-purple-600 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Premium</h3>
              <div className="text-right">
                <div className="text-2xl font-bold text-purple-600">$4.99</div>
                <div className="text-sm text-gray-600">/month</div>
              </div>
            </div>

            <ul className="space-y-2 mb-6">
              {plans[0].features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => {
                // In production, this would create checkout session
                navigate('/auth?upgrade=premium');
              }}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all font-semibold flex items-center justify-center gap-2"
              data-testid="upgrade-to-premium-button"
            >
              <Sparkles className="w-5 h-5" />
              Upgrade to Premium
            </button>
          </div>

          <p className="text-xs text-center text-gray-600">
            Cancel anytime • No long-term commitment
          </p>
        </div>
      </div>
    </div>
  );
}

// Premium Badge for locked features
export function PremiumBadge({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full text-xs font-medium hover:opacity-90 transition-opacity"
      data-testid="premium-badge"
    >
      <Lock className="w-3 h-3" />
      Premium
    </button>
  );
}

// Premium feature wrapper
export function PremiumFeature({ children, isPremium, featureName, description }) {
  const [showLock, setShowLock] = useState(false);

  if (isPremium) {
    return (
      <>
        <div
          onClick={() => setShowLock(true)}
          className="relative cursor-pointer"
          data-testid="premium-feature-locked"
        >
          <div className="opacity-50 pointer-events-none">
            {children}
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm rounded-lg">
            <div className="text-center">
              <Lock className="w-8 h-8 text-white mx-auto mb-2" />
              <PremiumBadge onClick={() => setShowLock(true)} />
            </div>
          </div>
        </div>
        {showLock && (
          <PremiumFeatureLock
            featureName={featureName}
            description={description}
            onClose={() => setShowLock(false)}
          />
        )}
      </>
    );
  }

  return children;
}
